import { cn } from "@/lib/utils/cn";
import { RecentPost } from "@/lib/api/interfaces/posts.interface";
import { History, LayoutTemplate, CheckCircle2, Calendar, RefreshCcw, UserCircle } from "lucide-react";
import { StandardGradientHeader } from "@/util-components/dialog/components/StandardGradientHeader";
import { Badge } from "@/ui-components/ui/badge";
import { format } from "date-fns";
import { de } from "date-fns/locale";
import { getSocialIcon, getSocialName, getSocialColorClasses } from "@/page-components/post-generator/utils/socialUtils";
import { Button } from "@/ui-components/ui/button";
import { useNavigate } from "react-router-dom";
import { usePostsGetPost } from "@/lib/api/requests/posts.requests";
import { MentionedEntitiesList } from "./MentionedEntitiesList";
import { DualImageDisplay } from "@/util-components/dialog/components/DualImageDisplay";
import { PermissionFeatureWrapper } from "@/util-components/auth/PermissionFeatureWrapper";
import { Permission } from "@/lib/api/interfaces/permissions.interface";
import { PlanKey } from "@/lib/api/interfaces/billing.interface";
import { DetailsBaseDialog } from "@/util-components/dialog/base-dialogs/details-base-dialog";
import { DialogTextSection, DialogContentSection, DialogContentItem } from "@/util-components/dialog/base-dialogs/dialog-sections";

interface RecentPostDialogProps {
  open: boolean;
  onClose: () => void;
  post: RecentPost | null;
}

export const RecentPostDialog = ({ open, onClose, post }: RecentPostDialogProps) => {
  const navigate = useNavigate();

  const { data: detailedPost, isLoading: isFetchingDetails } = usePostsGetPost(
    open && post ? post._id : undefined,
    { enabled: open && !!post }
  );

  if (!post) return null;

  const displayPost = detailedPost || post;

  const formatDateString = (dateString: string) => {
    try {
      return format(new Date(dateString), "dd. MMMM yyyy, HH:mm 'Uhr'", { locale: de });
    } catch {
      return dateString;
    }
  };

  const handleReuseTemplate = () => {
    onClose();
    if (displayPost.templateId) {
      navigate(`/post-generator/create-post?templateId=${displayPost.templateId}`);
    } else {
      navigate(`/post-generator/create-post`);
    }
  };

  const headerContent = (
    <StandardGradientHeader>
      <div className="flex flex-col items-center justify-center gap-2 w-full">
        <div className="w-full flex justify-center">
          <DualImageDisplay 
            templateUrl={displayPost.template?.thumbnailUrl} 
            postUrl={displayPost.thumbnailUrl} 
            templateId={displayPost.templateId}
          />
        </div>
        <Badge variant="outline" className="bg-white/20 text-white border-white/30 flex items-center gap-1.5 shadow-sm mt-1">
          <CheckCircle2 className="w-3 h-3 text-emerald-300" />
          Erfolgreich gepostet
        </Badge>
      </div>
    </StandardGradientHeader>
  );

  return (
    <DetailsBaseDialog
      open={open}
      onClose={onClose}
      icon={History}
      title="Vergangener Post"
      description="Details zu diesem veröffentlichten Post."
      maxWidth="xl"
      hideEdit={true}
      hideDelete={true}
      customDetailsActions={
        <PermissionFeatureWrapper requiredPermission={Permission.MANAGE_ENTITIES} requiredPlan={PlanKey.BASIC}>
          <Button onClick={handleReuseTemplate} size="default" className="h-11 px-6 sm:px-8 rounded-xl font-bold text-sm bg-blue-600 hover:bg-blue-700 text-white shadow-sm transition-all">
            <RefreshCcw className="w-4 h-4 mr-2" />
            <span className="hidden sm:inline">Template neu benutzen</span>
            <span className="sm:hidden">Neu benutzen</span>
          </Button>
        </PermissionFeatureWrapper>
      }
      detailsContent={
        <div className="space-y-6">
          {headerContent}
          
          <DialogContentSection title="Informationen" icon={History}>
            <div className="grid gap-3 sm:grid-cols-2">
              <DialogContentItem 
                icon={Calendar} 
                label="Veröffentlicht am" 
                value={<p className="text-sm leading-tight">{formatDateString(displayPost.createdAt)}</p>} 
              />

              <DialogContentItem 
                icon={LayoutTemplate} 
                label="Plattform" 
                value={
                  <div className="flex flex-wrap items-center gap-2 mt-0.5">
                    {displayPost.origin ? (
                      <div className={cn("flex items-center gap-1.5 px-2 py-1 rounded-md text-xs font-semibold", getSocialColorClasses(displayPost.origin))}>
                        {getSocialIcon(displayPost.origin, 'w-3 h-3')}
                        {getSocialName(displayPost.origin)}
                      </div>
                    ) : (
                       <span className="text-sm font-medium text-gray-500">Unbekannt</span>
                    )}
                  </div>
                } 
              />

              <DialogContentItem 
                icon={UserCircle} 
                label="Gepostet von" 
                value={
                  isFetchingDetails ? (
                    <div className="h-5 w-24 bg-gray-200 animate-pulse rounded mt-0.5"></div>
                  ) : displayPost.user ? (
                    <p className="text-sm leading-tight">
                      {displayPost.user.firstName} {displayPost.user.lastName}
                    </p>
                  ) : (
                    <span className="text-sm font-medium text-gray-500">Unbekannt</span>
                  )
                } 
              />
            </div>
          </DialogContentSection>

          {/* Mentioned Entities section */}
          <MentionedEntitiesList 
            entities={displayPost.mentionedEntities || []} 
            isLoading={isFetchingDetails} 
          />

          {/* Caption / Message */}
          {displayPost.message && (
            <div className="pt-2">
              <DialogTextSection 
                title="Bildunterschrift" 
                icon={LayoutTemplate} 
                text={displayPost.message} 
              />
            </div>
          )}
        </div>
      }
    />
  );
};

